<template>
  <div class="app-container" :class="{ 'dark-mode': theme === 'dark' }">
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
  </div>
</template>

<script>
import { provide, ref, onMounted, watch } from 'vue'

export default {
  name: 'app',
  setup() {
    const theme = ref('light')

    const setTheme = (newTheme) => {
      theme.value = newTheme
      localStorage.setItem('user-theme', newTheme)
      if (newTheme === 'dark') {
        document.body.classList.add('dark-mode')
      } else {
        document.body.classList.remove('dark-mode')
      }
    }

    const toggleTheme = () => {
      const newTheme = theme.value === 'light' ? 'dark' : 'light'
      setTheme(newTheme)
    }

    onMounted(() => {
      const savedTheme = localStorage.getItem('user-theme')
      const systemPreference = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      
      if (savedTheme) {
        setTheme(savedTheme)
      } else {
        setTheme(systemPreference)
      }

      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => {
        if (!localStorage.getItem('user-theme')) {
          setTheme(e.matches ? 'dark' : 'light')
        }
      })
    })

    provide('theme', theme)
    provide('toggleTheme', toggleTheme)

    return {
      theme
    }
  }
}
</script>

<style>
/* CSS Variables */
:root {
  --bg-body: #f4f6f9;
  --text-main: #333;
  --text-muted: #6c757d;
  --card-bg: #ffffff;
  --card-border: #f0f0f0;
  --card-shadow: rgba(0, 0, 0, 0.05);
  --input-bg: #fbfbfb;
  --input-border: #e0e0e0;
  --input-text: #333;
  --modal-bg: #ffffff;
  --dropdown-bg: #ffffff;
  --dropdown-hover: #e9ecef;
}

/* Dark Mode Variables */
.dark-mode {
  --bg-body: #121212;
  --text-main: #e0e0e0;
  --text-muted: #b0b3b8;
  --card-bg: #1e1e1e;
  --card-border: #333;
  --card-shadow: rgba(0, 0, 0, 0.5);
  --input-bg: #2d2d2d;
  --input-border: #444;
  --input-text: #e0e0e0;
  --modal-bg: #1e1e1e;
  --dropdown-bg: #2c2c2c;
  --dropdown-hover: #3a3a3a;
}

/* Global Styles & Reset overrides */
body {
  background-color: var(--bg-body) !important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: var(--text-main) !important;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-body); /* Ensure container matches body */
}

/* Typography Enhancements */
h1, h2, h3, h4, h5, h6 {
  font-weight: 700;
  color: var(--text-main) !important;
}

.text-muted {
  color: var(--text-muted) !important;
}

/* Card Styling - Elegant & Soft */
.card {
  border: none;
  border-radius: 16px;
  box-shadow: 0 4px 20px var(--card-shadow);
  background-color: var(--card-bg) !important;
  color: var(--text-main) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.3s ease;
}

.card-header {
  background-color: transparent !important;
  border-bottom: 1px solid var(--card-border) !important;
  font-weight: 600;
  padding: 1.25rem;
  color: var(--text-main) !important;
}

.card-body {
  padding: 1.5rem;
  color: var(--text-main) !important;
}

.card-footer {
  background-color: transparent !important;
  border-top: 1px solid var(--card-border) !important;
}

/* Button Styling */
.btn {
  border-radius: 50px; /* Pill shape */
  padding: 0.5rem 1.5rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.85rem;
  transition: all 0.3s ease;
}

.btn-primary {
  background-color: #4a90e2;
  border-color: #4a90e2;
}

.btn-primary:hover {
  background-color: #357abd;
  border-color: #357abd;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(74, 144, 226, 0.3);
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-danger {
  background-color: #e74c3c;
  border-color: #e74c3c;
}

/* Inputs */
.form-control {
  border-radius: 12px;
  padding: 0.75rem 1rem;
  border: 1px solid var(--input-border) !important;
  background-color: var(--input-bg) !important;
  color: var(--input-text) !important;
}

.form-control:focus {
  background-color: var(--input-bg) !important;
  box-shadow: 0 0 0 4px rgba(74, 144, 226, 0.1);
  border-color: #4a90e2 !important;
  color: var(--input-text) !important;
}

/* Modals */
.modal-content {
  background-color: var(--modal-bg) !important;
  color: var(--text-main) !important;
  border: 1px solid var(--card-border);
}

.modal-header, .modal-footer {
  border-color: var(--card-border) !important;
}

.btn-close {
  filter: var(--btn-close-filter);
}

.dark-mode .btn-close {
  filter: invert(1) grayscale(100%) brightness(200%);
}

/* Dropdowns */
.dropdown-menu {
  background-color: var(--dropdown-bg) !important;
  border-color: var(--card-border) !important;
}

.dropdown-item {
  color: var(--text-main) !important;
}

.dropdown-item:hover, .dropdown-item:focus {
  background-color: var(--dropdown-hover) !important;
  color: var(--text-main) !important;
}

/* Specific Bootstrap Overrides for Dark Mode Compatibility */
.bg-white {
  background-color: var(--card-bg) !important;
}

.bg-dark {
   background-color: #000 !important;
}

.text-primary {
  color: #4a90e2 !important;
}

.input-group-text {
  background-color: var(--input-bg);
  border-color: var(--input-border);
  color: var(--text-main);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
